// Generate random reviews for services and packages
interface Review {
  id: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  comment: string;
  date: string;
  location: string;
}

// Himachal Pradesh themed names
const himachaliNames = [
  'Rajesh Kumar', 'Priya Sharma', 'Amit Thakur', 'Neha Verma', 'Vikram Singh',
  'Anjali Chauhan', 'Rahul Negi', 'Pooja Rana', 'Sandeep Parmar', 'Kavita Bhardwaj',
  'Deepak Dhiman', 'Ritu Sood', 'Mohit Chandel', 'Suman Pathania', 'Arun Katoch',
  'Manisha Dogra', 'Vinod Kashyap', 'Sunita Guleria', 'Pankaj Mehta', 'Rekha Shastri',
  'Ramesh Devi', 'Geeta Thakur', 'Suresh Chauhan', 'Anita Verma', 'Vijay Rana',
  'Meera Singh', 'Rakesh Parmar', 'Lalita Negi', 'Ashok Dhiman', 'Kiran Sood'
];

// Himachal locations
const locations = [
  'Shimla', 'Manali', 'Dharamshala', 'Kullu', 'Mandi', 'Solan', 'Hamirpur',
  'Bilaspur', 'Chamba', 'Una', 'Kangra', 'Palampur', 'Kasauli', 'Dalhousie',
  'Joginder Nagar', 'Sundernagar', 'Nahan', 'Kinnaur', 'Rampur', 'Baddi'
];

// Simple hash function to generate consistent random seed from string
const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Seeded random number generator
const seededRandom = (seed: number) => {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};

// Profile pictures - Himachal Pradesh themed avatars (using UI Avatars)
const getAvatar = (name: string, seed: number): string | undefined => {
  // 60% chance of having avatar using seeded random
  if (seededRandom(seed) < 0.6) {
    const initials = name.split(' ').map(n => n[0]).join('');
    const colors = ['4F46E5', '059669', 'DC2626', 'D97706', '7C3AED', '0891B2'];
    const bgColor = colors[Math.floor(seededRandom(seed + 1) * colors.length)];
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${bgColor}&color=fff&size=128&bold=true`;
  }
  return undefined;
};

// English reviews
const englishReviews = [
  'Excellent service! Very professional and punctual.',
  'Great work quality. Highly recommend to everyone.',
  'Very satisfied with the service provided.',
  'Amazing experience. Will definitely book again.',
  'Professional team and good work ethics.',
  'Value for money. Good service quality.',
  'Timely completion and neat work.',
  'Friendly staff and efficient service.',
  'Good experience overall. Worth the price.',
  'Quality work at affordable rates.',
  'Impressed with the attention to detail.',
  'Quick response and excellent service.',
  'Very helpful and professional team.',
  'Clean work and timely service.',
  'Satisfied customer. Will recommend to friends.'
];

// Hinglish reviews (Hindi + English mix)
const hinglishReviews = [
  'Bahut accha service hai. Highly recommended!',
  'Ekdum mast kaam kiya. Very professional team.',
  'Behtreen service mili. Will book again pakka!',
  'Kafi acchi service hai. Worth the money.',
  'Gazab ka kaam kiya team ne. Superb quality!',
  'Bilkul time pe aaye aur accha kaam kiya.',
  'Best service in Himachal. Ekdum satisfied!',
  'Kamaal ka kaam hai bhai. Full marks!',
  'Badhiya experience raha. Strongly recommend.',
  'Perfect kaam kiya. No complaints at all!',
  'Shandaar service! Bahut professional team.',
  'Mast kaam hai. Value for money pakka!',
  'Top class service milti hai. Keep it up!',
  'Ekdum best! Will definitely use again.',
  'Zabardast experience tha. Full satisfied!',
  'Bahut accha lagta hai inki service. Recommended!',
  'Proper kaam karte hain. No tension at all.',
  'Kamaal ki service hai. Ekdum punctual!',
  'Best hai yaar. Sabko recommend karunga.',
  'Badhiya quality hai. Paisa wasool service!'
];

// Generate random date within last 6 months
const generateDate = (): string => {
  const now = new Date();
  const sixMonthsAgo = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
  const randomTime = sixMonthsAgo.getTime() + Math.random() * (now.getTime() - sixMonthsAgo.getTime());
  const date = new Date(randomTime);
  
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  
  return `${day} ${month} ${year}`;
};

// Generate reviews for a service/package
export const generateReviews = (serviceId: string, count?: number): Review[] => {
  const seed = hashCode(serviceId);
  let currentSeed = seed;
  
  // Generate count using seeded random (8-18 reviews)
  const reviewCount = count || Math.floor(seededRandom(currentSeed++) * 11) + 8;
  const reviews: Review[] = [];
  
  // Mix of English and Hinglish (50-50 split approximately)
  const allReviews = [...englishReviews, ...hinglishReviews];
  
  for (let i = 0; i < reviewCount; i++) {
    const nameIndex = Math.floor(seededRandom(currentSeed++) * himachaliNames.length);
    const name = himachaliNames[nameIndex];
    
    const ratingRand = seededRandom(currentSeed++);
    const rating = ratingRand < 0.7 ? 5 : (ratingRand < 0.9 ? 4.5 : 4); // Mostly 4.5-5 stars
    
    const review: Review = {
      id: `${serviceId}-review-${i}`,
      userName: name,
      userAvatar: getAvatar(name, currentSeed++),
      rating: rating,
      comment: allReviews[Math.floor(seededRandom(currentSeed++) * allReviews.length)],
      date: generateDate(),
      location: locations[Math.floor(seededRandom(currentSeed++) * locations.length)]
    };
    reviews.push(review);
  }
  
  // Sort by date (newest first)
  return reviews.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

// Get review count with rating breakdown
export const getReviewStats = (reviews: Review[]) => {
  const totalReviews = reviews.length;
  
  // Handle empty reviews case
  if (totalReviews === 0) {
    return {
      totalReviews: 0,
      avgRating: 5.0,
      ratingBreakdown: {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0,
      }
    };
  }
  
  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews;
  
  const ratingBreakdown = {
    5: reviews.filter(r => r.rating === 5).length,
    4: reviews.filter(r => Math.floor(r.rating) === 4).length,
    3: reviews.filter(r => Math.floor(r.rating) === 3).length,
    2: reviews.filter(r => Math.floor(r.rating) === 2).length,
    1: reviews.filter(r => Math.floor(r.rating) === 1).length,
  };
  
  return {
    totalReviews,
    avgRating: Math.round(avgRating * 10) / 10,
    ratingBreakdown
  };
};
